import { computed, inject, Injectable } from '@angular/core';
import { CustomRoute } from 'src/app/pages/custom-route/interfaces/custom-route.interface';
import { CustomRouteService } from 'src/app/pages/custom-route/services/custom-route.service';
import { PlannerDayStateService } from 'src/app/pages/custom-route/shared/planner/services';
import { EditRouteService } from 'src/app/pages/custom-route/shared/planner/components/edit-route/services/edit-route.service';
import { catchError, map, of, tap } from 'rxjs';
import { BottomSheetConfig, MatBottomSheetAddNewPointOrReplacePointData } from 'src/app/interfaces/mat.interface';
import { SuggestedPointsComponent } from 'src/app/pages/custom-route/shared/planner/components/edit-route/components/suggested-points/suggested-points.component';
import { TranslateService } from '@ngx-translate/core';
import { EditedStatus } from 'src/app/pages/custom-route/shared/planner/components/edit-route/interfaces/edit-route.interface';
import { BreakpointsService } from 'src/app/services/breakpoints.service';
import { MatBottomSheetService } from 'src/app/services/mat-bottom-sheet.service';
import { SuggestedPointsService } from 'src/app/pages/custom-route/shared/planner/components/edit-route/components/suggested-points/services/suggested-points.service';
import dayjs from 'dayjs';

const TIMESLOT = 15;

type Result = { is_successful: true, content?: string } | { is_successful: false; reason: string };

@Injectable({ providedIn: 'root' })

export class GetFreeSlotsService {
    private _customRouteService: CustomRouteService = inject(CustomRouteService);
    private _plannerDayStateService: PlannerDayStateService = inject(PlannerDayStateService);
    private _editRouteService: EditRouteService = inject(EditRouteService);
    private _breakpointsService = inject(BreakpointsService);
    private _translateService = inject(TranslateService);
    private _matBottomSheetService = inject(MatBottomSheetService);
    private _suggestedPointsService = inject(SuggestedPointsService);

    private customRouteData = computed<CustomRoute>(() => this._customRouteService.customRouteData());
    private dayToShowIndex = computed<number>(() => this._plannerDayStateService.dayToShowIndex());
    private isMobile = computed(() => this._breakpointsService.isMobile());

    public getFreeSlots(day?: number) {
        const dayIndex = day ?? this.dayToShowIndex();
        const points = this.customRouteData()?.dailyRoutes?.[dayIndex]?.points;
        if (!points)
            return [];

        const slotDay = dayjs(this.customRouteData()?.dailyRoutes?.[dayIndex]?.date)?.format('YYYY-MM-DD');
        const token = this._customRouteService.token();

        const available: Record<string, boolean> = {};
        const endTime = dayjs('1970-01-01T23:45:00');
        let cursor = dayjs('1970-01-01T09:00:00');

        while (cursor.isSameOrBefore(endTime)) {
            const stringHour = cursor.format('HH:mm');
            available[stringHour] = true;

            for (const point of points) {
                const s = dayjs(point.startDate);
                const e = dayjs(point.endDate);
                const startHour = dayjs(`1970-01-01T${s.format('HH:mm')}:00`);
                let endHour = dayjs(`1970-01-01T${e.format('HH:mm')}:00`);

                if (endHour.isBefore(startHour)) 
                    endHour = endHour.add(1, 'day');

                const slotStart = dayjs(`1970-01-01T${stringHour}:00`);
                const slotEnd = slotStart.add(TIMESLOT, 'minute');
                const overlaps = slotStart.isBetween(startHour, endHour, 'minute', '[]') ||
                    slotEnd.isBetween(startHour, endHour, 'minute', '()') ||
                    (slotStart.isBefore(startHour) && slotEnd.isAfter(endHour)); 

                if (overlaps) {
                    available[stringHour] = false;
                    break;
                }
            }

            cursor = cursor.add(TIMESLOT, 'minute');
        }

        const freeSlots = this.computeDurations(available, TIMESLOT)?.map(e => ({
            ...e, 
            duration: e.duration - TIMESLOT
        }))?.filter(e => e.duration)?.map(e => {
            const startTimeString = dayjs(e.startTime, 'HH:mm').subtract(TIMESLOT, 'minute')?.format('HH:mm');
            const startTime = dayjs(`1970-01-01T${startTimeString}:00`);
            const prevPoint = points.slice().reverse().find(point => {
                const start = dayjs(point.startDate);
                const startHourPoint = dayjs(`1970-01-01T${start.format('HH:mm')}:00`);
                return startTime.isAfter(startHourPoint);
            })

            const endTimeString = dayjs(e.endTime, 'HH:mm').subtract(TIMESLOT, 'minute')?.format('HH:mm');
            const endTime = dayjs(`1970-01-01T${endTimeString}:00`);
            const nextPoint = points.find(point => {
                const end = dayjs(point.endDate);
                const endHourPoint = dayjs(`1970-01-01T${end.format('HH:mm')}:00`);
                return endTime.isBefore(endHourPoint);
            })

            return {
                ...e,
                token,
                day: slotDay,
                prevPoint: prevPoint?.id ? {
                    id: prevPoint?.id,
                    title: prevPoint?.title,
                    type: prevPoint?.type
                } : null,
                nextPoint:  nextPoint?.id ? {
                    id: nextPoint?.id,
                    title: nextPoint?.title,
                    type: prevPoint?.type
                } : null
            }
        })

        return freeSlots;
    }

    public getAvailableItemsOnSlot(day, hour){
        const freeSlots = this.getFreeSlots(day);
        const selectedSlot: any = freeSlots.find(slot => slot.startTime === hour);
        if (!selectedSlot)
            return of<Result>({ 
                is_successful: false, 
                reason: 'No hay espacio disponible a la hora seleccionada' 
            });

        return this._editRouteService.getSuggestedPoints(selectedSlot).pipe(
            tap(async (res: any) => {
                this._suggestedPointsService.suggestedPoints.set(res.points);
                this._suggestedPointsService.interestList.set(res.categoryList);
                await this._openSuggestedPointsBottomSheet(selectedSlot?.startTime);
            }),
            map((res: any): Result => ({ 
                is_successful: true,
                content: JSON.stringify(res?.points?.map(({ img, ...rest }) => rest))
            })),
            catchError(() =>
                of<Result>({ 
                    is_successful: false, 
                    reason: 'No hemos encontrado más recursos que se adapten en esta franja horaria' 
                })
            )
        )
    }

    private async _openSuggestedPointsBottomSheet(startTime: any): Promise<void> {
        const bottomSheetConfig: BottomSheetConfig<SuggestedPointsComponent, MatBottomSheetAddNewPointOrReplacePointData> = {
            component: SuggestedPointsComponent,
            data: {
                title: this._translateService.instant('Añadir nuevo recurso'),
                dayToShowIndex: this.dayToShowIndex(),
                orderIndex: 0,
                status: EditedStatus.ADDED,
                fromChatBot: true,
                startDate: startTime
            },
            panelClass: this.isMobile() 
                ? 'suggested-points-bottom-sheet--mobile' 
                : 'suggested-points-bottom-sheet--desktop',
        }
        
        await this._matBottomSheetService.openMatBottomSheet(bottomSheetConfig);
    }

    private computeDurations(available: Record<string, boolean>, stepMin = TIMESLOT): Array<{ startTime: string; endTime: string; duration: number }> {
        const labels = Object.keys(available).sort((a, b) => this.hhmmToMin(a) - this.hhmmToMin(b));
        const freeSlots: Array<{ startTime: string; endTime: string; duration: number }> = [];

        for (let i = 0; i < labels.length; i++) {
            const lbl = labels[i];
            if (!available[lbl]) 
                continue;

            let k = i;
            while (k < labels.length && available[labels[k]]) 
                k++;

            const duration = (k - i) * stepMin;
            const startMin = this.hhmmToMin(labels[i]);
            const endMin = startMin + duration;
            freeSlots.push({
                startTime: labels[i],
                endTime: this.minToHhmm(endMin),
                duration,
            });
        }

        return freeSlots;
    }

    private hhmmToMin(hhmm: string): number {
        const [h, m] = hhmm.split(':').map(Number);
        return h * 60 + m;
    }

    private minToHhmm(min: number): string {
        const h = Math.floor(min / 60);
        const m = min % 60;
        return String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
    }

}